"use client";
import React from "react";
import { Nunito_Sans } from "next/font/google";
import { Header } from "@/shared/index";
import { usePathname } from "next/navigation";
import { Company } from "@/modules/company/domain/ICompany";
import { IUserRole } from "@/modules/users/domain/IUser";
import { IBase } from "@umahealth/entities";
import { cn } from "@/utils/cn";
import { ReactQueryClientProvider } from "@/config/QueryClient";
import { CollaboratorSessionWrapper } from "@/context/CollaboratorSessionContext";
import "./globals.css";

const nunito = Nunito_Sans({
	weight: ["200", "300", "400", "500", "700", "900"],
	subsets: ["latin"],
	variable: "--font-family-primary",
});

export type ChildrenProps = {
	children: React.ReactNode;
	/** null cuando la API no responde: el layout degrada en vez de romper. */
	currentCompany: Company | null;
	userRoles: IUserRole[] | null;
	baseHabitual?: number | null;
	bases?: IBase[];
	/**
	 * Si el contexto de sesión de colaborador tiene que montarse.
	 *
	 * Lo decide el layout raíz, que es el que tiene los roles del usuario. Se
	 * monta **acá** y no alrededor de `children` porque el Header, que es donde
	 * viven los controles de turno, se dibuja como hermano de `children`: desde
	 * afuera del contexto no podía leerlo.
	 */
	esColaborador?: boolean;
};

const TemplateLayout = ({ children, currentCompany, userRoles, bases, baseHabitual, esColaborador = false }: ChildrenProps) => {
	const pathname = usePathname();
	const isLogin = pathname === "/usuarios/acceso";
	const isPrint = pathname === "/operaciones/imprimir";
	/**
	 * La credencial del operador es lo que se abre al escanear el QR, casi
	 * siempre desde un celular y sin sesión de la app. El sidebar ahí no sirve
	 * para nada y encima se come parte del ancho, así que se muestra sola.
	 */
	const isOperatorCredential = /^\/usuarios\/[^/]+\/ultimaOperacion$/.test(
		pathname,
	);
	const hideChrome = isPrint || isOperatorCredential;
	return (
		<html>
			<head lang="es">
				<link rel="icon" href="/favicons/favicon.ico" />
			</head>
			<body className={`${nunito.variable}`}>
				{/*
				  * Los providers envuelven al Header **y** a `children`. Estaban
				  * alrededor de `children` solamente, y como el Header es su hermano
				  * quedaba fuera del contexto: los controles de turno no se dibujaban
				  * nunca. `CollaboratorSessionWrapper` no monta nada si
				  * `esColaborador` es false, así que para el resto de los perfiles
				  * esto no cambia en nada.
				  */}
				<ReactQueryClientProvider>
				<CollaboratorSessionWrapper enabled={esColaborador}>
				<main
					className={cn(
						"w-dvw flex h-screen overflow-hidden",
						pathname.includes("acceso")
							? "bg-secondary-600"
							: "bg-[#ECF0F2]"
					)}
				>
					{!hideChrome && (
					<div className="sticky top-0 h-screen self-start flex-shrink-0 print:hidden">
						<Header
							logged={!isLogin ? true : false}
							currentCompany={currentCompany}
							userRoles={userRoles}
							bases={bases}
							baseHabitual={baseHabitual}
						/>
					</div>
					)}
					<div className={cn(
						"flex justify-center items-start w-full max-w-full bg-grey-25 overflow-y-auto h-screen",
						// En un teléfono el sidebar ya se lleva ~60 px de ancho fijo;
						// con 32 px de padding a cada lado, el contenido se queda con
						// poco más de la mitad de la pantalla. Se achica en móvil y
						// vuelve al respiro original desde `sm`.
						isPrint ? "p-0 bg-white" : "py-4 px-4 sm:py-8 sm:px-8"
					)}>
						{children}
					</div>
				</main>
				</CollaboratorSessionWrapper>
				</ReactQueryClientProvider>
			</body>
		</html>
	);
};

export default TemplateLayout;
